import { company } from '@/config/company';
import { env } from '@/config/env';
import { accessorySourceLabels } from '@/content/accessories';
import { type Accessory } from '@/services/accessories/types';
import { formatUgx } from '@/utils/vehicleFormat';

export type ShareChannel = 'whatsapp' | 'x' | 'copy';

/** X counts every link as 23 characters, plus the space before it. */
const X_LIMIT = 280;
const X_LINK_COST = 24;
const MAX_MAKES = 3;

/** Where the shared link lands, tagged so a visit can be traced to its channel. */
export function accessoryShareUrl(
  accessory: Pick<Accessory, 'slug'>,
  channel: ShareChannel,
  origin: string,
): string {
  const params = new URLSearchParams({ utm_source: channel, utm_medium: 'share' });
  return `${origin}/accessories/${accessory.slug}?${params.toString()}`;
}

/** The site's own origin, or this page's when no public URL is configured. */
export function shareOrigin(): string {
  if (env.siteUrl) return env.siteUrl;
  return typeof window === 'undefined' ? '' : window.location.origin;
}

function fitmentLine(fitment: Accessory['fitment']): string {
  if (fitment.universal) return 'Fits most vehicles';
  if (fitment.makes.length === 0) return '';
  const shown = fitment.makes.slice(0, MAX_MAKES).join(', ');
  const more = fitment.makes.length > MAX_MAKES ? ' and more' : '';
  return `Fits ${shown}${more}${fitment.note ? ` (${fitment.note})` : ''}`;
}

const stockLines: Record<Accessory['stock'], string> = {
  'in-stock': 'In stock in Kampala',
  'on-order': 'Arriving with our next shipment',
  'made-to-order': 'Ordered for you on request',
};

/** +256780905191 → '+256 780 905 191'; anything else is shown as configured. */
function whatsappNumberLine(): string | null {
  const e164 = company.whatsappE164;
  if (!e164) return null;
  const grouped = e164.match(/^\+256(\d{3})(\d{3})(\d{3})$/);
  return `Order or ask on WhatsApp: ${grouped ? `+256 ${grouped[1]} ${grouped[2]} ${grouped[3]}` : e164}`;
}

const priceLine = (accessory: Accessory) =>
  accessory.priceUgx !== null ? formatUgx(accessory.priceUgx) : 'Price on request';

/**
 * The message shared on WhatsApp and copied by Copy link, one fact per line so it can be read at a glance.
 *
 * Written for a Kampala buyer forwarding it to a friend or a family group: the
 * part and its price first, then the three questions the reply would otherwise
 * be — is it genuine, is it here, will it fit my car — and the link last, on a
 * line of its own, so WhatsApp draws the product photo beneath it. Asterisks
 * are WhatsApp's bold. No emoji: a pictograph that fails to survive the trip
 * through a link or a clipboard arrives as a replacement character.
 */
export function accessoryShareMessage(accessory: Accessory, link: string): string {
  return [
    `*${accessory.name}*`,
    `${accessory.brand} · ${accessorySourceLabels[accessory.source]}`,
    `*${priceLine(accessory)}*`,
    fitmentLine(accessory.fitment) || null,
    stockLines[accessory.stock],
    '',
    'Kabex Motors, Kampala. We confirm it fits your car before you pay, fit it for you, and deliver anywhere in Uganda.',
    '',
    'See photos and full details:',
    link,
    whatsappNumberLine(),
  ]
    .filter((line): line is string => line !== null)
    .join('\n');
}

/** The short form for X: one sentence, with the link supplied separately. */
export function accessoryXText(accessory: Accessory): string {
  return [
    `${accessory.name} (${accessory.brand}) — ${priceLine(accessory)}.`,
    fitmentLine(accessory.fitment) && `${fitmentLine(accessory.fitment)}.`,
    `${stockLines[accessory.stock]} with Kabex Motors, Kampala.`,
  ]
    .filter(Boolean)
    .join(' ');
}

const truncate = (text: string, max: number) =>
  text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;

export function whatsappShareHref(accessory: Accessory, origin: string): string {
  const link = accessoryShareUrl(accessory, 'whatsapp', origin);
  return `https://wa.me/?text=${encodeURIComponent(accessoryShareMessage(accessory, link))}`;
}

export function xShareHref(accessory: Accessory, origin: string): string {
  const params = new URLSearchParams({
    text: truncate(accessoryXText(accessory), X_LIMIT - X_LINK_COST),
    url: accessoryShareUrl(accessory, 'x', origin),
  });
  return `https://x.com/intent/post?${params.toString()}`;
}
